<script setup lang="ts">
import type { GalleryMeta } from '../../../types/gallery-meta'
import type { TopicMeta, TopicPageData } from '../../../types/topic-meta'
import { imageUrl } from '../../utils/url.ts'

const route = useRoute()
const router = useRouter()
const toast = useToast()
const { loggedIn } = useUserSession()

const topicPath = computed(() => {
  const value = route.params.path
  return (Array.isArray(value) ? value : [value]).filter((segment): segment is string => typeof segment === 'string' && !!segment)
})

const endpoint = computed(() => `/api/topics/${topicPath.value.join('/')}`)
const fetchKey = computed(() => `topic-page:${topicPath.value.join('/')}`)

const { data, error, refresh } = await useFetch<TopicPageData>(() => endpoint.value, {
  key: fetchKey,
  watch: [topicPath]
})

if (error.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Topic not found'
  })
}

const isCreateSubtopicOpen = ref(false)
const isCreateGalleryOpen = ref(false)
const isEditTopicOpen = ref(false)

const creatingSubtopic = ref(false)
const creatingGallery = ref(false)
const savingTopic = ref(false)
const deletingTopic = ref(false)
const deletingTopicIds = ref<string[]>([])

const subtopicTitle = ref('')
const subtopicName = ref('')
const subtopicDescription = ref('')
const subtopicHidden = ref(false)

const galleryTitle = ref('')
const galleryName = ref('')
const galleryDescription = ref('')
const galleryHidden = ref(false)
const galleryPassword = ref('')
const subtopicPreviewFile = ref<File | null>(null)
const subtopicPreviewObjectUrl = ref('')
const subtopicPreviewInputRef = ref<HTMLInputElement | null>(null)
const subtopicPreviewDropZoneRef = ref<HTMLElement | null>(null)

const editTopicTitle = ref('')
const editTopicName = ref('')
const editTopicDescription = ref('')
const editTopicHidden = ref(false)
const editTopicPreviewFile = ref<File | null>(null)
const editTopicPreviewObjectUrl = ref('')
const editTopicPreviewInputRef = ref<HTMLInputElement | null>(null)
const editTopicPreviewDropZoneRef = ref<HTMLElement | null>(null)
const editTopicPreviewPath = ref<string | null>(null)
const editTopicRemoveExistingPreview = ref(false)

const topic = computed(() => data.value?.topic as TopicMeta)
const subtopics = computed(() => data.value?.subtopics || [])
const visibleSubtopics = computed(() => loggedIn.value ? subtopics.value : subtopics.value.filter(subtopic => !subtopic.hidden))
const galleries = computed(() => data.value?.galleries || [])
const visibleGalleries = computed(() => loggedIn.value ? galleries.value : galleries.value.filter(gallery => !gallery.hidden))
const topicBackgroundImagePath = ref<string | null>(null)
const breadcrumb = computed(() => data.value?.pathTopics || [])
const breadcrumbItems = computed(() => breadcrumb.value.map((segment, index) => ({
  label: segment.title,
  to: topicLinkAt(index)
})))
const showBreadcrumb = computed(() => breadcrumb.value.length > 1)

watch([visibleSubtopics, visibleGalleries], ([subtopicValue, visibleGalleryValue]) => {
  const galleryPreviewPaths = visibleGalleryValue
    .map(gallery => gallery.uploadedPreviewImagePath || gallery.previewImagePath)
    .filter((path): path is string => !!path)

  const topicPreviewPaths = subtopicValue
    .map(subtopic => subtopic.previewImagePath)
    .filter((path): path is string => !!path)

  const previewPaths = galleryPreviewPaths.length ? galleryPreviewPaths : topicPreviewPaths

  if (!previewPaths.length) {
    topicBackgroundImagePath.value = null
    return
  }

  const randomIndex = Math.floor(Math.random() * previewPaths.length)
  topicBackgroundImagePath.value = previewPaths[randomIndex] ?? null
}, { immediate: true })

watch(subtopicTitle, (value) => {
  subtopicName.value = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
})

watch(galleryTitle, (value) => {
  galleryName.value = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
})

watch(subtopicPreviewFile, (file) => {
  if (subtopicPreviewObjectUrl.value) {
    URL.revokeObjectURL(subtopicPreviewObjectUrl.value)
    subtopicPreviewObjectUrl.value = ''
  }

  if (file) {
    subtopicPreviewObjectUrl.value = URL.createObjectURL(file)
  }
})

watch(editTopicPreviewFile, (file) => {
  if (editTopicPreviewObjectUrl.value) {
    URL.revokeObjectURL(editTopicPreviewObjectUrl.value)
    editTopicPreviewObjectUrl.value = ''
  }

  if (file) {
    editTopicPreviewObjectUrl.value = URL.createObjectURL(file)
  }
})

onBeforeUnmount(() => {
  if (subtopicPreviewObjectUrl.value) {
    URL.revokeObjectURL(subtopicPreviewObjectUrl.value)
  }

  if (editTopicPreviewObjectUrl.value) {
    URL.revokeObjectURL(editTopicPreviewObjectUrl.value)
  }
})

function setSubtopicPreviewFile(file?: File | null) {
  if (!file || !file.type.startsWith('image/')) {
    return
  }

  subtopicPreviewFile.value = file
}

function setEditTopicPreviewFile(file?: File | null) {
  if (!file || !file.type.startsWith('image/')) {
    return
  }

  editTopicPreviewFile.value = file
}

const { isOverDropZone: isSubtopicPreviewDropZoneOver } = useDropZone(subtopicPreviewDropZoneRef, (files) => {
  setSubtopicPreviewFile(files?.[0] || null)
})

const { isOverDropZone: isEditTopicPreviewDropZoneOver } = useDropZone(editTopicPreviewDropZoneRef, (files) => {
  setEditTopicPreviewFile(files?.[0] || null)
})

function clearSubtopicPreviewSelection() {
  subtopicPreviewFile.value = null
  if (subtopicPreviewInputRef.value) {
    subtopicPreviewInputRef.value.value = ''
  }
}

function clearEditTopicPreviewSelection() {
  editTopicPreviewFile.value = null
  if (editTopicPreviewInputRef.value) {
    editTopicPreviewInputRef.value.value = ''
  }
}

function openSubtopicPreviewPicker() {
  subtopicPreviewInputRef.value?.click()
}

function openEditTopicPreviewPicker() {
  editTopicPreviewInputRef.value?.click()
}

function onSubtopicPreviewChange(event: Event) {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  setSubtopicPreviewFile(file)
}

function onEditTopicPreviewChange(event: Event) {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  setEditTopicPreviewFile(file)
}

function markEditTopicPreviewForRemoval() {
  editTopicRemoveExistingPreview.value = true
  editTopicPreviewPath.value = null
}

async function uploadTopicPreview(topicId: string, file: File) {
  const formData = new FormData()
  formData.append('topicId', topicId)
  formData.append('file', file)

  await $fetch<{ topic: TopicMeta }>('/api/topics/preview', {
    method: 'POST',
    body: formData
  })
}

async function removeTopicPreview(topicId: string) {
  await $fetch<{ topic: TopicMeta }>('/api/topics/preview', {
    method: 'DELETE',
    body: { topicId }
  })
}

function topicLinkAt(index: number): string {
  const segments = breadcrumb.value.slice(0, index + 1).map(item => item.name)
  return `/topics/${segments.join('/')}`
}

function subtopicLink(subtopic: TopicMeta): string {
  const topicSegments = breadcrumb.value.map(item => item.name)
  return `/topics/${[...topicSegments, subtopic.name].join('/')}`
}

function galleryLink(gallery: GalleryMeta): string {
  const topicSegments = breadcrumb.value.map(item => item.name)
  return `/topics/${topicSegments.join('/')}/galleries/${gallery.name}`
}

function openEditTopicModal() {
  if (!topic.value) {
    return
  }

  editTopicTitle.value = topic.value.title || ''
  editTopicName.value = topic.value.name || ''
  editTopicDescription.value = topic.value.description || ''
  editTopicHidden.value = !!topic.value.hidden
  editTopicPreviewPath.value = topic.value.previewImagePath || null
  editTopicRemoveExistingPreview.value = false
  clearEditTopicPreviewSelection()
  isEditTopicOpen.value = true
}

async function updateTopic() {
  if (!topic.value?.id || !editTopicTitle.value.trim() || savingTopic.value) {
    return
  }

  savingTopic.value = true

  await $fetch<{ topic: TopicMeta, path: string[] }>(`/api/topics/${topic.value.id}`, {
    method: 'PATCH',
    body: {
      title: editTopicTitle.value,
      name: editTopicName.value,
      description: editTopicDescription.value,
      hidden: editTopicHidden.value
    }
  }).then(async (updated) => {
    if (editTopicRemoveExistingPreview.value && topic.value?.previewImagePath) {
      await removeTopicPreview(topic.value.id).catch((err) => {
        toast.add({
          color: 'red',
          title: 'Topic updated, but preview removal failed',
          description: err.data?.message || err.message
        })
      })
    }

    if (editTopicPreviewFile.value) {
      await uploadTopicPreview(topic.value.id, editTopicPreviewFile.value).catch((err) => {
        toast.add({
          color: 'red',
          title: 'Topic updated without new preview image',
          description: err.data?.message || err.message
        })
      })
    }

    isEditTopicOpen.value = false
    await refresh()

    const nextPath = `/topics/${updated.path.join('/')}`
    if (nextPath !== route.path) {
      await router.push(nextPath)
    }
  }).catch((err) => {
    toast.add({
      color: 'red',
      title: 'Failed to update topic',
      description: err.data?.message || err.message
    })
  }).finally(() => {
    savingTopic.value = false
  })
}

async function createSubtopic() {
  if (!topic.value?.id || !subtopicTitle.value.trim() || creatingSubtopic.value) {
    return
  }

  creatingSubtopic.value = true

  await $fetch<TopicMeta>('/api/topics', {
    method: 'POST',
    body: {
      title: subtopicTitle.value,
      name: subtopicName.value,
      description: subtopicDescription.value,
      hidden: subtopicHidden.value,
      parentId: topic.value.id
    }
  }).then(async (created) => {
    if (subtopicPreviewFile.value) {
      await uploadTopicPreview(created.id, subtopicPreviewFile.value).catch((err) => {
        toast.add({
          color: 'red',
          title: 'Sub-topic created without preview image',
          description: err.data?.message || err.message
        })
      })
    }

    isCreateSubtopicOpen.value = false
    subtopicTitle.value = ''
    subtopicName.value = ''
    subtopicDescription.value = ''
    subtopicHidden.value = false
    clearSubtopicPreviewSelection()
    await refresh()
    await router.push(`${route.path}/${created.name}`)
  }).catch((err) => {
    toast.add({
      color: 'red',
      title: 'Failed to create sub-topic',
      description: err.data?.message || err.message
    })
  }).finally(() => {
    creatingSubtopic.value = false
  })
}

async function createGallery() {
  if (!topic.value?.id || !galleryTitle.value.trim() || creatingGallery.value) {
    return
  }

  creatingGallery.value = true

  await $fetch<GalleryMeta>('/api/galleries', {
    method: 'POST',
    body: {
      title: galleryTitle.value,
      name: galleryName.value,
      description: galleryDescription.value,
      topicId: topic.value.id,
      hidden: galleryHidden.value,
      password: galleryPassword.value
    }
  }).then(async (created) => {
    isCreateGalleryOpen.value = false
    galleryTitle.value = ''
    galleryName.value = ''
    galleryDescription.value = ''
    galleryHidden.value = false
    galleryPassword.value = ''
    await refresh()
    await router.push(galleryLink(created))
  }).catch((err) => {
    toast.add({
      color: 'red',
      title: 'Failed to create gallery',
      description: err.data?.message || err.message
    })
  }).finally(() => {
    creatingGallery.value = false
  })
}

async function deleteTopic(topicToDelete: TopicMeta) {
  if (!loggedIn.value || !topicToDelete.canDelete || deletingTopic.value) {
    return
  }

  if (!window.confirm(`Delete topic "${topicToDelete.title}"? This cannot be undone.`)) {
    return
  }

  deletingTopic.value = true

  await $fetch(`/api/topics/${topicToDelete.id}`, {
    method: 'DELETE'
  }).then(async () => {
    isEditTopicOpen.value = false

    const parentSegments = breadcrumb.value.slice(0, -1).map(item => item.name)
    const nextPath = parentSegments.length ? `/topics/${parentSegments.join('/')}` : '/'

    await router.push(nextPath)
  }).catch((err) => {
    toast.add({
      color: 'red',
      title: 'Failed to delete topic',
      description: err.data?.message || err.message
    })
  }).finally(() => {
    deletingTopic.value = false
  })
}

async function deleteSubtopic(topicToDelete: TopicMeta) {
  if (!loggedIn.value || !topicToDelete.canDelete || deletingTopicIds.value.includes(topicToDelete.id)) {
    return
  }

  if (!window.confirm(`Delete topic "${topicToDelete.title}"? This cannot be undone.`)) {
    return
  }

  deletingTopicIds.value = [...deletingTopicIds.value, topicToDelete.id]

  await $fetch(`/api/topics/${topicToDelete.id}`, {
    method: 'DELETE'
  }).then(async () => {
    await refresh()
  }).catch((err) => {
    toast.add({
      color: 'red',
      title: 'Failed to delete topic',
      description: err.data?.message || err.message
    })
  }).finally(() => {
    deletingTopicIds.value = deletingTopicIds.value.filter(id => id !== topicToDelete.id)
  })
}
</script>

<template>
  <main class="relative isolate min-h-screen p-6 md:p-10 pt-28 md:pt-32 flex flex-col">
    <div
      v-if="topicBackgroundImagePath"
      class="pointer-events-none fixed inset-0 z-0"
      aria-hidden="true"
    >
      <img
        :src="imageUrl(topicBackgroundImagePath, 'full')"
        alt=""
        class="h-full w-full object-cover"
      >
      <div class="absolute inset-0 bg-gradient-to-b from-black/60 via-black/75 to-black/90" />
    </div>

    <div class="relative z-10 max-w-6xl mx-auto w-full space-y-10 flex-1">
      <div class="space-y-4 text-center">
        <AppBreadcrumbs
          v-if="showBreadcrumb"
          :items="breadcrumbItems"
          centered
        />

        <div class="flex items-center justify-center pt-10">
          <h1 class="text-3xl font-semibold text-white">
            {{ topic?.title }}
          </h1>
        </div>
        <div class="flex flex-wrap items-center justify-center gap-2">
          <UModal
            v-if="loggedIn"
            v-model:open="isEditTopicOpen"
            title="Edit Topic"
            :close="{ color: 'gray', variant: 'outline', class: 'rounded-full' }"
          >
            <UButton
              icon="i-heroicons-pencil-square-20-solid"
              color="primary"
              variant="subtle"
              label="Edit"
              size="xs"
              aria-label="Edit topic"
              @click="openEditTopicModal"
            />
            <template #body>
              <form
                class="flex flex-col gap-4"
                @submit.prevent="updateTopic"
              >
                <UInput
                  v-model="editTopicTitle"
                  placeholder="Title"
                  required
                />
                <UInput
                  v-model="editTopicName"
                  placeholder="Slug"
                />
                <input
                  ref="editTopicPreviewInputRef"
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="onEditTopicPreviewChange"
                >
                <UTextarea
                  v-model="editTopicDescription"
                  placeholder="Short description"
                  :rows="3"
                />
                <UCheckbox
                  v-model="editTopicHidden"
                  label="Hidden (not shown in parent topic listing)"
                />
                <div class="space-y-2">
                  <p class="text-sm text-gray-300">
                    Topic preview image
                  </p>
                  <div
                    v-if="editTopicPreviewPath && !editTopicPreviewObjectUrl"
                    class="flex items-center gap-3 rounded-lg border border-white/10 bg-black/30 p-3"
                  >
                    <img
                      :src="imageUrl(editTopicPreviewPath, 'thumb')"
                      alt="Current topic preview"
                      class="h-14 w-14 rounded-md object-cover border border-white/10"
                    >
                    <UButton
                      type="button"
                      size="xs"
                      color="red"
                      variant="soft"
                      label="Remove"
                      class="ml-auto"
                      @click="markEditTopicPreviewForRemoval"
                    />
                  </div>
                  <div
                    v-else-if="editTopicPreviewObjectUrl"
                    class="flex items-center gap-3 rounded-lg border border-white/10 bg-black/30 p-3"
                  >
                    <img
                      :src="editTopicPreviewObjectUrl"
                      alt="Selected topic preview"
                      class="h-14 w-14 rounded-md object-cover border border-white/10"
                    >
                    <UButton
                      type="button"
                      size="xs"
                      color="red"
                      variant="soft"
                      label="Remove"
                      class="ml-auto"
                      @click="clearEditTopicPreviewSelection"
                    />
                  </div>
                  <button
                    v-else
                    ref="editTopicPreviewDropZoneRef"
                    type="button"
                    class="w-full rounded-lg border border-dashed p-4 text-sm transition-colors text-left"
                    :class="isEditTopicPreviewDropZoneOver ? 'border-primary-400 bg-primary-500/10 text-white' : 'border-white/20 bg-black/20 text-gray-300 hover:border-white/40'"
                    @click="openEditTopicPreviewPicker"
                  >
                    Click or drag & drop an image
                  </button>
                </div>
                <div class="flex justify-end">
                  <UButton
                    v-if="topic?.canDelete"
                    type="button"
                    color="red"
                    variant="soft"
                    label="Delete Topic"
                    class="mr-auto"
                    :loading="deletingTopic"
                    @click="topic && deleteTopic(topic)"
                  />
                  <UButton
                    type="submit"
                    :loading="savingTopic"
                    label="Save"
                    color="primary"
                  />
                </div>
              </form>
            </template>
          </UModal>
          <UModal
            v-if="loggedIn"
            v-model:open="isCreateSubtopicOpen"
            title="Create Sub-Topic"
            :close="{ color: 'gray', variant: 'outline', class: 'rounded-full' }"
          >
            <UButton
              label="New Sub-Topic"
              icon="i-heroicons-plus"
              color="primary"
              variant="subtle"
              size="xs"
            />
            <template #body>
              <form
                class="flex flex-col gap-4"
                @submit.prevent="createSubtopic"
              >
                <UInput
                  v-model="subtopicTitle"
                  placeholder="Title"
                  required
                />
                <UInput
                  v-model="subtopicName"
                  placeholder="Slug (auto-generated, editable)"
                />
                <input
                  ref="subtopicPreviewInputRef"
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="onSubtopicPreviewChange"
                >
                <UTextarea
                  v-model="subtopicDescription"
                  placeholder="Short description"
                  :rows="3"
                />
                <UCheckbox
                  v-model="subtopicHidden"
                  label="Hidden (not shown in parent topic listing)"
                />
                <div class="space-y-2">
                  <p class="text-sm text-gray-300">
                    Topic preview image
                  </p>
                  <div
                    v-if="subtopicPreviewObjectUrl"
                    class="flex items-center gap-3 rounded-lg border border-white/10 bg-black/30 p-3"
                  >
                    <img
                      :src="subtopicPreviewObjectUrl"
                      alt="Selected sub-topic preview"
                      class="h-14 w-14 rounded-md object-cover border border-white/10"
                    >
                    <div class="flex gap-2 ml-auto">
                      <UButton
                        type="button"
                        size="xs"
                        color="neutral"
                        variant="soft"
                        label="Replace"
                        @click="openSubtopicPreviewPicker"
                      />
                      <UButton
                        type="button"
                        size="xs"
                        color="red"
                        variant="soft"
                        label="Remove"
                        @click="clearSubtopicPreviewSelection"
                      />
                    </div>
                  </div>
                  <button
                    v-else
                    ref="subtopicPreviewDropZoneRef"
                    type="button"
                    class="w-full rounded-lg border border-dashed p-4 text-sm transition-colors text-left"
                    :class="isSubtopicPreviewDropZoneOver ? 'border-primary-400 bg-primary-500/10 text-white' : 'border-white/20 bg-black/20 text-gray-300 hover:border-white/40'"
                    @click="openSubtopicPreviewPicker"
                  >
                    Click or drag & drop an image
                  </button>
                </div>
                <div class="flex justify-end">
                  <UButton
                    type="submit"
                    :loading="creatingSubtopic"
                    label="Create"
                    color="primary"
                  />
                </div>
              </form>
            </template>
          </UModal>
          <UModal
            v-if="loggedIn"
            v-model:open="isCreateGalleryOpen"
            title="Create Gallery"
            :close="{ color: 'gray', variant: 'outline', class: 'rounded-full' }"
          >
            <UButton
              label="New Gallery"
              icon="i-heroicons-photo"
              color="primary"
              variant="subtle"
              size="xs"
            />
            <template #body>
              <form
                class="flex flex-col gap-4"
                @submit.prevent="createGallery"
              >
                <UInput
                  v-model="galleryTitle"
                  placeholder="Title"
                  required
                />
                <UInput
                  v-model="galleryName"
                  placeholder="Slug (auto-generated, editable)"
                />
                <UTextarea
                  v-model="galleryDescription"
                  placeholder="Short description"
                  :rows="3"
                />
                <UCheckbox
                  v-model="galleryHidden"
                  label="Hidden (not shown in topic gallery listing)"
                />
                <UInput
                  v-model="galleryPassword"
                  type="password"
                  autocomplete="new-password"
                  placeholder="Optional gallery password"
                />
                <div class="flex justify-end">
                  <UButton
                    type="submit"
                    :loading="creatingGallery"
                    label="Create"
                    color="primary"
                  />
                </div>
              </form>
            </template>
          </UModal>
        </div>
        <p class="text-gray-300 max-w-3xl mx-auto">
          {{ topic?.description || '' }}
        </p>
      </div>

      <section
        v-if="visibleSubtopics.length"
        class="space-y-4"
      >
        <h2 class="text-xl font-medium text-white text-center">
          Categories
        </h2>
        <div
          class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4"
        >
          <NuxtLink
            v-for="subtopic in visibleSubtopics"
            :key="subtopic.id"
            :to="subtopicLink(subtopic)"
            class="group relative overflow-hidden rounded-xl border border-white/10 bg-black/40 p-4 sm:p-5 hover:border-white/30 transition-colors min-h-48 sm:min-h-56 flex flex-col justify-end"
          >
            <UButton
              v-if="loggedIn && subtopic.canDelete"
              icon="i-heroicons-trash-20-solid"
              color="red"
              variant="soft"
              size="xs"
              class="absolute top-3 right-3 z-20 opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Delete topic"
              :loading="deletingTopicIds.includes(subtopic.id)"
              @click.prevent.stop="deleteSubtopic(subtopic)"
            />
            <img
              v-if="subtopic.previewImagePath"
              :src="imageUrl(subtopic.previewImagePath, 'thumb')"
              :alt="subtopic.title"
              class="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-[filter] duration-300"
            >
            <div
              v-if="subtopic.previewImagePath"
              class="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/20"
            />
            <div class="relative z-10 mt-auto space-y-2">
              <h3 class="text-white text-base sm:text-lg font-medium break-words leading-snug">
                {{ subtopic.title }}
              </h3>
              <p class="text-gray-200 line-clamp-3">
                {{ subtopic.description || '' }}
              </p>
            </div>
          </NuxtLink>
        </div>
      </section>

      <section
        v-if="visibleGalleries.length"
        class="space-y-4"
      >
        <h2 class="text-xl font-medium text-white text-center">
          Galleries
        </h2>
        <div
          class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4"
        >
          <NuxtLink
            v-for="gallery in visibleGalleries"
            :key="gallery.id"
            :to="galleryLink(gallery)"
            class="group relative overflow-hidden rounded-xl border border-white/10 bg-black/40 p-4 sm:p-5 hover:border-white/30 transition-colors min-h-48 sm:min-h-56 flex flex-col justify-end"
          >
            <img
              v-if="gallery.uploadedPreviewImagePath || gallery.previewImagePath"
              :src="imageUrl(gallery.uploadedPreviewImagePath || gallery.previewImagePath || '', 'thumb')"
              :alt="gallery.title"
              class="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-[filter] duration-300"
            >
            <div
              v-if="gallery.uploadedPreviewImagePath || gallery.previewImagePath"
              class="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/20"
            />
            <div
              v-if="gallery.isPasswordProtected || (gallery.hidden && loggedIn)"
              class="absolute top-3 left-3 z-20 flex items-center gap-1 rounded-md border border-white/20 bg-black/45 px-2 py-1 text-gray-200"
            >
              <UTooltip
                v-if="gallery.hidden && loggedIn"
                text="Hidden gallery"
              >
                <UIcon
                  name="i-heroicons-eye-slash-20-solid"
                  class="w-4 h-4"
                />
              </UTooltip>
              <UTooltip
                v-if="gallery.isPasswordProtected"
                text="Password protected"
              >
                <UIcon
                  name="i-heroicons-lock-closed-20-solid"
                  class="w-4 h-4"
                />
              </UTooltip>
            </div>
            <div class="relative z-10 mt-auto space-y-2">
              <h3 class="text-white text-base sm:text-lg font-medium break-words leading-snug">
                {{ gallery.title }}
              </h3>
              <p class="text-gray-200 line-clamp-3">
                {{ gallery.description || '' }}
              </p>
            </div>
          </NuxtLink>
        </div>
      </section>
    </div>

    <AppCopyrightFooter />
  </main>
</template>
